import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { TrendingUp, TrendingDown, PiggyBank } from "lucide-react";
import { useMemo } from "react";
import { Transaction } from "@/lib/types";

interface SavingsRateCardProps {
  transactions: Transaction[];
}

export default function SavingsRateCard({ transactions }: SavingsRateCardProps) {
  const savingsRate = useMemo(() => {
    const now = new Date();
    const currentMonth = now.getMonth();
    const currentYear = now.getFullYear();

    const income = transactions
      .filter((transaction) => {
        const transactionDate = new Date(transaction.date);
        return (
          transaction.type === "income" &&
          transactionDate.getMonth() === currentMonth &&
          transactionDate.getFullYear() === currentYear
        );
      })
      .reduce((total, transaction) => total + transaction.amount, 0);

    const expenses = transactions
      .filter((transaction) => {
        const transactionDate = new Date(transaction.date);
        return (
          transaction.type === "expense" &&
          transactionDate.getMonth() === currentMonth &&
          transactionDate.getFullYear() === currentYear
        );
      })
      .reduce((total, transaction) => total + transaction.amount, 0);

    if (income === 0) return 0;
    return ((income - expenses) / income) * 100;
  }, [transactions]);

  // [T38P0] Calculate real previous-month savings rate
  const previousMonthRate = useMemo(() => {
    const now = new Date();
    const curYear = now.getFullYear();
    const curMonth = now.getMonth();
    const prevMonth = curMonth === 0 ? 11 : curMonth - 1;
    const prevYear = curMonth === 0 ? curYear - 1 : curYear;

    const prevIncome = transactions
      .filter((t) => {
        const d = new Date(t.date);
        return (
          t.type === 'income' &&
          d.getFullYear() === prevYear &&
          d.getMonth() === prevMonth
        );
      })
      .reduce((s, t) => s + t.amount, 0);

    const prevExpenses = transactions
      .filter((t) => {
        const d = new Date(t.date);
        return (
          t.type === 'expense' &&
          d.getFullYear() === prevYear &&
          d.getMonth() === prevMonth
        );
      })
      .reduce((s, t) => s + t.amount, 0);

    return prevIncome > 0
      ? ((prevIncome - prevExpenses) / prevIncome) * 100
      : null; // null = no previous data
  }, [transactions]);

  const changePercentage =
    previousMonthRate !== null
      ? (savingsRate - previousMonthRate).toFixed(1)
      : null;
  const isPositive =
    changePercentage !== null ? parseFloat(changePercentage) >= 0 : true;

  return (
    <Card
      data-section="savings-rate"
      data-testid="home-card-kpi"
      className="h-full min-h-0 flex flex-col transition-all duration-200 hover:shadow-md hover:border-primary/20 glass-panel"
    >
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2 shrink-0">
        <CardTitle className="text-sm font-medium">Savings Rate</CardTitle>
        <div className="p-2 bg-blue-500/10 rounded-full">
          <PiggyBank className="h-4 w-4 text-blue-500" />
        </div>
      </CardHeader>
      <CardContent className="flex-1 min-h-0 overflow-hidden">
        <div className="text-lg sm:text-2xl font-bold truncate">
          {savingsRate.toFixed(1)}%
        </div>
        <p className="text-xs text-muted-foreground flex items-center gap-1 mt-1">
          {changePercentage === null ? (
            <span>No previous month data</span>
          ) : (
            <>
              {isPositive ? (
                <TrendingUp className="h-3 w-3 text-green-500" />
              ) : (
                <TrendingDown className="h-3 w-3 text-red-500" />
              )}
              <span className={isPositive ? 'text-green-500' : 'text-red-500'}>
                {isPositive ? '+' : ''}
                {changePercentage}%
              </span>
              <span>from last month</span>
            </>
          )}
        </p>
      </CardContent>
    </Card>
  );
}
